




















































import { Component, Vue } from 'vue-property-decorator';

import { IPeriod } from '@/models/period';

import flagKeys from '@/constants/flagKeys';

import PerformanceUtils from '@/utils/PerformanceUtils';
import DateUtils from '@/utils/DateUtils';

import { EInitiativesDictionary } from '@/models/initiatives';

import SectionWrapper from '@/componentsV2/molecules/SectionWrapper/SectionWrapper.vue';
import DateFilter from '@/components/molecules/DateFilter/DateFilter.vue';
import Tabs from '@/componentsV2/molecules/Tabs/Tabs.vue';
import ArrowWithBorder from '@/components/atoms/ArrowWithBorder/ArrowWithBorder.vue';

// Tab components
import ActivitiesOverviewLoyaltyRewards from '@/componentsV2/organisms/ActivitiesOverviewLoyaltyRewards/ActivitiesOverviewLoyaltyRewards.vue';
import ActivitiesOverviewReferralProgram from '@/componentsV2/organisms/ActivitiesOverviewReferralProgram/ActivitiesOverviewReferralProgram.vue';
import ActivitiesOverviewFreeCoinsCampaigns from '@/componentsV2/organisms/ActivitiesOverviewFreeCoinsCampaigns/ActivitiesOverviewFreeCoinsCampaigns.vue';
import ActivitiesOverviewDealsDiscountsAndBirthdayClub from '@/componentsV2/organisms/ActivitiesOverviewDealsDiscountsAndBirthdayClub/ActivitiesOverviewDealsDiscountsAndBirthdayClub.vue';
import ActivitiesOverviewSellingCoins from '@/componentsV2/organisms/ActivitiesOverviewSellingCoins/ActivitiesOverviewSellingCoins.vue';

import { applyPerformanceDataFilter } from '@/views/Performance/flag-helpers';

@Component({
  components: {
    SectionWrapper,
    DateFilter,
    ArrowWithBorder,
    Tabs,
    ActivitiesOverviewLoyaltyRewards,
    ActivitiesOverviewReferralProgram,
    ActivitiesOverviewFreeCoinsCampaigns,
    ActivitiesOverviewDealsDiscountsAndBirthdayClub,
    ActivitiesOverviewSellingCoins
  }
})
export default class PerformanceActivitiesOverview extends Vue {
  activeTabKey: EInitiativesDictionary =
    this.activityState.campaignActivityActiveTabKey || 'loyalty-rewards';
  showRequestLoading =
    !this.activityState.list.length && !this.prevRouteIsCustomerDetail;

  period: IPeriod = PerformanceUtils.defaultPeriod();
  filters: Record<string, any> = {
    ...PerformanceUtils.defaultPeriodForFilter()
  };

  get isMobileDevice() {
    return this.$mobileCheck();
  }

  get tPrefix() {
    return `performance.activities.campaignActivity`;
  }

  get tabMapper(): {
    [k: string]: any;
  } {
    return {
      [EInitiativesDictionary.LOYALTY_AND_REWARDS]: {
        label: String(this.$t(`${this.tPrefix}.tabs.loyaltyRewards.title`)),
        component: 'ActivitiesOverviewLoyaltyRewards'
      },
      [EInitiativesDictionary.FREE_COINS]: {
        label: String(this.$t(`${this.tPrefix}.tabs.freeCoinsCampaigns.title`)),
        component: 'ActivitiesOverviewFreeCoinsCampaigns'
      },
      [EInitiativesDictionary.WELCOME_COINS]: {
        label: String(
          this.$t(`${this.tPrefix}.tabs.welcomeCoinsCampaigns.title`)
        ),
        component: 'ActivitiesOverviewFreeCoinsCampaigns'
      },
      [EInitiativesDictionary.REFERRAL_PROGRAM]: {
        label: String(this.$t(`${this.tPrefix}.tabs.referralProgram.title`)),
        component: 'ActivitiesOverviewReferralProgram'
      },
      [EInitiativesDictionary.DEALS_AND_DISCOUNTS]: {
        label: String(this.$t(`${this.tPrefix}.tabs.dealsDiscounts.title`)),
        component: 'ActivitiesOverviewDealsDiscountsAndBirthdayClub',
        componentProps: {
          offerType: 'General'
        }
      },
      [EInitiativesDictionary.BIRTHDAY_CLUB]: {
        label: String(this.$t(`${this.tPrefix}.tabs.birthdayClub.title`)),
        component: 'ActivitiesOverviewDealsDiscountsAndBirthdayClub',
        componentProps: {
          offerType: 'Birthday'
        }
      },
      [EInitiativesDictionary.SPECIAL_COINS]: {
        label: String(this.$t(`${this.tPrefix}.tabs.specialCoins.title`)),
        component: 'ActivitiesOverviewDealsDiscountsAndBirthdayClub',
        componentProps: {
          offerType: 'SpecialCoinOffers'
        }
      },
      [EInitiativesDictionary.GIFT_COINS]: {
        label: String(this.$t(`${this.tPrefix}.tabs.giftCardsCoins.title`)),
        component: 'ActivitiesOverviewSellingCoins'
      }
    };
  }

  get flags() {
    return this.$store.getters['flags/getFlags'] || [];
  }

  get businessReferralIsActive() {
    return this.flags.includes(flagKeys.businessReferral);
  }

  get tabs() {
    const specialCoinsExcludeTabs = [
      EInitiativesDictionary.BIRTHDAY_CLUB,
      EInitiativesDictionary.DEALS_AND_DISCOUNTS,
      EInitiativesDictionary.FREE_COINS
    ];
    return Object.entries(this.tabMapper)
      .map(([key, value]) => ({
        key,
        label: value.label
      }))
      .filter(
        ({ key }) =>
          !specialCoinsExcludeTabs.includes(key as EInitiativesDictionary)
      )
      .filter(
        applyPerformanceDataFilter(
          this.businessReferralIsActive,
          'key',
          flagKeys.referralProgram
        )
      );
  }

  get filteredTabs() {
    return PerformanceUtils.filteredDataBySlug(this.tabs, 'key');
  }

  get activityState() {
    return this.$store.getters['performance/getActivityState'];
  }

  get prevRouteIsCustomerDetail() {
    return (
      window.previousRouteName &&
      window.previousRouteName === 'performance-customer-center-detail'
    );
  }

  handlePeriodChange(period: IPeriod) {
    this.period = PerformanceUtils.normalizedPeriod(period);
    this.handleFilter({ from: this.period.start, to: this.period.end });

    const formatDate = (date: string) =>
      DateUtils.formattedDate({
        date,
        applyTimezone: true
      });

    this.$analytics({
      eventAction: 'change',
      eventCategory: 'Activities Overview - Period',
      eventLabel: `${formatDate(period.start)} - ${formatDate(period.end)}`
    });
  }

  handleFilter(filter: Object = {}) {
    this.filters = {
      ...this.filters,
      ...filter
    };
  }

  handleTabChanges(tabKey: EInitiativesDictionary) {
    if (tabKey !== this.activeTabKey) {
      this.showRequestLoading = true;
      this.$store.dispatch(
        'performance/SET_CAMPAIGN_ACTIVITY_ACTIVE_TAB_KEY',
        tabKey
      );
      this.activeTabKey = tabKey;
      this.filters = { from: this.period.start, to: this.period.end };

      this.$analytics({
        eventAction: 'click',
        eventCategory: 'Activities Overview - Tag Filter',
        eventLabel: tabKey
      });
    }
  }
}
